import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { GOOGLE_AUTH_ADAPTER } from '../tokens/infrastructure.tokens';
import { NotificationService } from '@app/services/notification.service';
import { TranslationService } from '../services/translation.service';

export const oauthCallbackGuard: CanActivateFn = async (route) => {
  const router = inject(Router);
  const googleAuth = inject(GOOGLE_AUTH_ADAPTER);
  const notification = inject(NotificationService);
  const translation = inject(TranslationService);

  const redirect = (message?: string, navState?: Record<string, unknown>) => {
    if (message) {
      notification.error(message);
    }
    const target = (navState?.['redirect'] as string) || '/';
    router.navigateByUrl(target, navState ? { state: navState } : undefined);
    return false;
  };

  const code = route.queryParamMap.get('code');
  const state = route.queryParamMap.get('state');
  const error = route.queryParamMap.get('error');

  if (error) {
    // Expected: user cancelled the OAuth consent screen
    console.warn('OAuth cancelled by user:', error);
    return redirect(translation.instant('common.notifications.oauth_cancelled'));
  }

  if (!code || !state) {
    // Unexpected: malformed or replayed callback URL (possible CSRF probe)
    console.warn('OAuth callback missing code or state parameter');
    return redirect(translation.instant('common.notifications.oauth_invalid_params'));
  }

  try {
    const result = await googleAuth.handleOAuthCallback(code, state);
    const customState = result?.customState || {};
    if (result?.accessToken) {
      console.log('✓ OAuth authorization successful');
      return redirect(undefined, {
        oauthSuccess: true,
        ...customState,
      });
    }

    return redirect(translation.instant('common.notifications.oauth_failed'), {
      oauthSuccess: false,
      ...customState,
    });
  } catch (err) {
    console.error('OAuth callback error:', err);
    return redirect(translation.instant('common.notifications.oauth_error'));
  }
};
